/**
 * AtlasAreaLightsModule — LTC rect area lights on the lit windows at night
 * (shader additions wave, 2026-09-15).
 *
 * The engine already carries three's production LTC path (`AreaLightsModule`
 * / `initLTC` upload the two 64² LUTs so `RectAreaLightNode` is not black);
 * Atlas never placed an emitter. This module keeps a small POOL of
 * `RectAreaLight`s and seats them on the nearest glass panes — the interior-
 * mapped windows that light up after dusk — as rectangular emitters whose
 * radiance follows the night and the lit-window ratio (`areaLightIntensity`).
 * A rectangle's polygon integral already accounts for its solid angle and
 * distance; no point-light falloff is layered on top.
 *
 * THE LIGHT-SET IDENTITY LAW applies: the pool is created at init (only when
 * the flag is on — the menu row says "reload"), every light stays permanently
 * visible, an unused slot is intensity 0 parked at y = −10000. Only uniforms
 * and transforms change per frame.
 *
 * Panes are found by a throttled scene walk (every 45 frames, within
 * `AREA_LIGHT_RANGE_M`); the emitter faces the pane's thinnest local axis,
 * flipped toward the player, sized to the pane. An unshadowed rectangle lights
 * through walls; the range and the pool bound how far that can reach — a
 * first scope, stated as such.
 *
 * FRAME: pane positions are read off `matrixWorld` (render space) and the
 * lights are scene children (absolute) — `absolute = world − scene.position`.
 *
 * Lever: `__areaLights()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { initLTC } from 'enginex/render/lighting/area/ltcRuntime.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor } from './SkyModule.js';
import { activePreset } from '../lib/quality_tiers.js';
import { isGlassMaterialName, litRatioForNight } from '../lib/stage3_fx.js';
import { AREA_LIGHT_POOL, AREA_LIGHT_RANGE_M, areaLightIntensity, stage5Stats, paneEligible } from '../lib/stage5_fx.js';

/** The interior a pane belongs to: the nearest ancestor tagged by BuildingInteriorModule, else null (an exterior pane). */
function interiorIdOf(o: THREE.Object3D): string | null {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) { const id = (p.userData as { interiorId?: unknown }).interiorId; if (typeof id === 'string') return id; }
  return null;
}

const PARK_Y = -10000;
const SCAN_EVERY = 45;
const COLOR = 0xffd9a3;

interface Pane { x: number; y: number; z: number; nx: number; ny: number; nz: number; w: number; h: number; d2: number }

export class AtlasAreaLightsModule implements IModule {
  readonly name = 'AreaLights';
  private app!: App;
  private readonly lights: THREE.RectAreaLight[] = [];
  private frame = 0;
  private panes: Pane[] = [];
  private readonly _p = new THREE.Vector3();
  private readonly _q = new THREE.Quaternion();
  private readonly _s = new THREE.Vector3();
  private readonly _n = new THREE.Vector3();
  private readonly _box = new THREE.Box3();

  init(app: App): void {
    this.app = app;
    if (!activePreset().areaLights) return; // the pool's existence is fixed for the session
    try { initLTC(); } catch { /* LTC LUTs unavailable: the lights render black, nothing else breaks */ }
    for (let i = 0; i < AREA_LIGHT_POOL; i++) {
      const l = new THREE.RectAreaLight(COLOR, 0, 2, 3);
      l.position.set(0, PARK_Y, 0);
      app.world.scene.add(l);
      this.lights.push(l);
    }
    app.world.addSystem(makeSystem('AtlasAreaLights', Phase.RenderSub, 11, () => this.tick()));
    (globalThis as unknown as { __areaLights?: () => unknown }).__areaLights = () => ({
      pool: this.lights.length,
      seated: this.lights.filter((l) => l.position.y > PARK_Y + 1).length,
      panes: this.panes.length,
      intensity: stage5Stats.areaLightIntensity,
      lights: this.lights.map((l) => ({ x: +l.position.x.toFixed(1), y: +l.position.y.toFixed(1), z: +l.position.z.toFixed(1), i: +l.intensity.toFixed(2), w: +l.width.toFixed(2), h: +l.height.toFixed(2) })),
    });
  }

  private scan(): void {
    const scene = this.app.world.scene;
    const sp = scene.position;
    const px = atlas.localX + sp.x, py = atlas.localY + sp.y, pz = atlas.localZ + sp.z; // the player in RENDER space
    const found: Pane[] = [];
    const r2 = AREA_LIGHT_RANGE_M * AREA_LIGHT_RANGE_M;
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.visible || found.length >= 64) return;
      const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material | undefined;
      if (!mat || !isGlassMaterialName(mat.name)) return;
      // Render wave 1 (2026-09-17): indoors, only the active interior's own panes (paneEligible).
      if (!paneEligible(interiorIdOf(m), atlas.interiorId)) return;
      m.matrixWorld.decompose(this._p, this._q, this._s);
      const dx = this._p.x - px, dy = this._p.y - py, dz = this._p.z - pz;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > r2) return;
      if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
      const bb = m.geometry.boundingBox!;
      this._box.copy(bb);
      const ex = (bb.max.x - bb.min.x) * this._s.x, ey = (bb.max.y - bb.min.y) * this._s.y, ez = (bb.max.z - bb.min.z) * this._s.z;
      // the pane's normal = its thinnest local axis
      const axis = ex <= ey && ex <= ez ? 0 : ey <= ez ? 1 : 2;
      this._n.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0).applyQuaternion(this._q).normalize();
      // face the player
      if (this._n.x * dx + this._n.y * dy + this._n.z * dz > 0) this._n.negate();
      const w = axis === 0 ? ez : ex, h = axis === 1 ? ez : ey;
      this._p.x += (bb.min.x + bb.max.x) * 0.5 * this._s.x; this._p.y += (bb.min.y + bb.max.y) * 0.5 * this._s.y; this._p.z += (bb.min.z + bb.max.z) * 0.5 * this._s.z;
      found.push({ x: this._p.x, y: this._p.y, z: this._p.z, nx: this._n.x, ny: this._n.y, nz: this._n.z, w: Math.max(0.5, Math.min(6, w)), h: Math.max(0.5, Math.min(6, h)), d2 });
    });
    found.sort((a, b) => a.d2 - b.d2);
    this.panes = found.slice(0, AREA_LIGHT_POOL);
    // Seat the pool (absolute = render − scene.position), 5 cm off the pane, facing outward.
    for (let i = 0; i < this.lights.length; i++) {
      const l = this.lights[i], pane = this.panes[i];
      if (!pane) { l.position.set(0, PARK_Y, 0); continue; }
      l.position.set(pane.x + pane.nx * 0.05 - sp.x, pane.y + pane.ny * 0.05 - sp.y, pane.z + pane.nz * 0.05 - sp.z);
      l.width = pane.w; l.height = pane.h;
      l.lookAt(l.position.x + pane.nx, l.position.y + pane.ny, l.position.z + pane.nz);
    }
    stage5Stats.areaLightsSeated = this.panes.length;
  }

  private tick(): void {
    if (!this.lights.length) return;
    this.frame++;
    const on = !!activePreset().areaLights && !atlas.spaceFlight && atlas.currentWorldId != null;
    if (!on) { for (const l of this.lights) l.intensity = 0; return; }
    if (this.frame % SCAN_EVERY === 0) this.scan();
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    const intensity = areaLightIntensity(night, litRatioForNight(night));
    stage5Stats.areaLightIntensity = intensity;
    for (let i = 0; i < this.lights.length; i++) this.lights[i].intensity = this.panes[i] ? intensity : 0;
  }
}
